import {useEffect, useRef, useState} from 'react';
import {useStoreActions, useStoreState} from 'easy-peasy';
import axios from 'axios';
import resolveToCallable from './resolveToCallable';
import {Resolver, UseDataParams} from './types';
import {navigate} from '@reach/router';
import SessionManager from '../services/SessionManager';
// Setup axios interceptor to track API requests for session management
axios.interceptors.request.use((config) => {
    // Only track requests to our API endpoints (not external URLs)
    if (config.url && (
        config.url.startsWith('/') ||
        config.url.includes(window.location.hostname) ||
        config.url.includes('/internal_api/') ||
        config.url.includes('/api/')
    )) {
        try {
            const sessionManager = SessionManager.getInstance();
            sessionManager.trackApiRequest();
        } catch (error) {
            // Silently fail if SessionManager is not available
            console.debug('SessionManager not available for request tracking');
        }
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});


type UseDataLoading<T> = [T | null, true, ((_: any) => any), Error | null];
type UseDataReady<T> = [T, boolean, ((_: any) => any), Error | null];

const useData = <T, StoreModel extends {} = {}>(
    url: string | null,
    storeResolverOrParams: Resolver | UseDataParams,
    actionResolver_: Resolver = (actions) => actions.store,
    dataResolver_: Resolver = (state) => state.items,
): UseDataReady<T> => {
    let actionResolver: Resolver;
    let dataResolver: Resolver;
    let storeResolver: Resolver;
    let setLoadingResolver: Resolver = null;
    let loadingResolver: Resolver = null;
    let requestParams: Object = {};
    let useLocalStore: boolean = false;
    let initialValue: any = null;
    let reloadOnParamsChange: boolean = true;
    let useCachedData: boolean = false;
    let raiseErrors: boolean = true;
    let destroyOnUnmount: boolean = false;

    const [localLoading, setLocalLoading] = useState<boolean>(false);

    if (storeResolverOrParams === null || storeResolverOrParams === undefined) {
        throw Error('Either storeResolver or params for useData must be specified');
    } else if (['function', 'string'].includes(typeof storeResolverOrParams)) {
        storeResolver = <Resolver>storeResolverOrParams;
        actionResolver = actionResolver_;
        dataResolver = dataResolver_;
    } else if (typeof storeResolverOrParams === 'object') {
        ({useLocalStore = useLocalStore} = storeResolverOrParams);

        ({
            storeResolver,
            requestParams = requestParams,
            actionResolver = useLocalStore ? null : (actions: any) => actions.store,
            dataResolver = useLocalStore ? null : (state: any) => state.items,
            loadingResolver = loadingResolver,
            setLoadingResolver = setLoadingResolver,
            initialValue = initialValue,
            reloadOnParamsChange = reloadOnParamsChange,
            useCachedData = useCachedData,
            raiseErrors = raiseErrors,
            destroyOnUnmount = destroyOnUnmount,
        } = storeResolverOrParams);
    }

    const [data, setData] = useState<any>(initialValue);
    const [error, setError] = useState<Error | null>(null);
    const [isMounted, setIsMounted] = useState<boolean>(true);

    let remoteLoadingState;
    let storeAction: (_: any) => any;
    let dataState;

    // Handle authentication errors
    if (error?.stack && error?.stack.includes('401')) {
        navigate(window.URLS?.logOutView || '/auth/login');
        return [null as T, false, () => {}, error];
    }

    if (!useLocalStore) {
        remoteLoadingState = loadingResolver !== null && setLoadingResolver !== null;
    } else {
        remoteLoadingState = false;
    }

    if (useCachedData && useLocalStore) {
        if (window.FCONST.DEBUG) {
            console.warn('Cannot used cached data without a specified redux store (state is in local scope)');
        }
        useCachedData = false;
    }

    if (useCachedData && Object.keys(requestParams).length !== 0) {
        if (window.FCONST.DEBUG) {
            console.warn(
                'Using useCachedData along with requestParams can produce unexpected results because individual '
                + 'caching has not been implemented for each requestParams set. Make sure you take this into account.',
            );
        }
    }

    const callableStoreResolver = resolveToCallable(storeResolver);
    const callableActionResolver = resolveToCallable(actionResolver);
    const callableDataResolver = resolveToCallable(dataResolver);
    const callableLoadingResolver = resolveToCallable(loadingResolver);
    const callableSetLoadingResolver = resolveToCallable(setLoadingResolver);
    const callableReloadResolver = resolveToCallable(useLocalStore ? null : 'reload');
    const callableSetReloadResolver = resolveToCallable(useLocalStore ? null : 'setReload');

    const resolvedStoreActions = (actions: any) => callableStoreResolver(actions);
    const resolvedStoreState = (state: any) => callableStoreResolver(state);

    const storeActionFromStore = useStoreActions((actions) => callableActionResolver(resolvedStoreActions(actions)));
    const dataStateFromStore = useStoreState((state) => callableDataResolver(resolvedStoreState(state)));

    const loadingFromStore = useStoreState((state) => callableLoadingResolver(resolvedStoreState(state)));
    const setLoadingFromStore = useStoreActions((actions) => callableSetLoadingResolver(resolvedStoreActions(actions)));

    const reloadFromStore = useStoreState((state) => callableReloadResolver(resolvedStoreState(state)));
    const setReloadFromStore = useStoreActions((actions) => callableSetReloadResolver(resolvedStoreActions(actions)));

    if (useLocalStore) {
        storeAction = setData;
        dataState = data;
    } else {
        storeAction = storeActionFromStore;
        dataState = dataStateFromStore;
    }

    let loading: boolean;
    let setLoading: (loading: boolean) => boolean | void;

    if (!remoteLoadingState) {
        loading = localLoading;
        setLoading = setLocalLoading;
    } else {
        loading = loadingFromStore;
        setLoading = setLoadingFromStore;
    }

    const requestParamsRef = useRef(requestParams);
    requestParamsRef.current = requestParams;

    let requestParamsJSON = '';
    if (reloadOnParamsChange) {
        requestParamsJSON = JSON.stringify(requestParams);
    }

    let initiallyLoaded = false;

    if (useCachedData && !useLocalStore && dataState && Object.keys(dataState).length) {
        initiallyLoaded = true;
    }

    const [loaded, setLoaded] = useState<boolean>(initiallyLoaded);

    const reload = useLocalStore ? false : reloadFromStore;
    const setReload = useLocalStore ? null : setReloadFromStore;

    if (
        !useLocalStore
        && !initiallyLoaded
        && !loaded
        && (
            (dataState === undefined && initialValue === null)
            || initialValue !== null
        )
    ) {
        dataState = initialValue;
    }

    useEffect(() => {
        setIsMounted(true);
        return () => {
            setIsMounted(false);
            if (destroyOnUnmount) {
                storeAction(initialValue);
                setLoading(false);
                setLoaded(false);
                setError(null);
            }
        };
    }, [
        destroyOnUnmount,
        storeAction,
        initialValue,
        setLoading,
        setLoaded,
        setError,
    ]);

    useEffect(() => {
        if (useCachedData && !useLocalStore && dataState && Object.keys(dataState).length > 0 && !reload) {
            if (!loaded) {
                setLoaded(true);
            }
            return;
        }

        const fetchData = async () => {
            const lockObtained = setLoading(true);
            if (lockObtained === false || !url) {
                return;
            }

            try {
                const response = await axios.get(url, requestParamsRef.current);
                if (isMounted) {
                    storeAction(response.data);
                }
            } catch (e: any) {
                if (isMounted) {
                    setError(e);
                }

                // Historically this hook threw when raiseErrors was true, but that
                // can leave consumers stuck in a loading state. Instead, we just
                // log in debug mode and rely on the error state.
                if (raiseErrors && (window as any).FCONST?.DEBUG) {
                    // eslint-disable-next-line no-console
                    console.error('useData request failed:', e);
                }
            } finally {
                if (isMounted) {
                    if (!useLocalStore && typeof setReload === 'function') {
                        setReload(false);
                    }
                    setLoaded(true);
                    setLoading(false);
                }
            }
        };

        if (url && !loading && (!loaded || reload)) {
            fetchData();
        }
    }, [
        storeAction,
        setLoading,
        url,
        loading,
        loaded,
        dataState,
        useCachedData,
        useLocalStore,
        reload,
        ...(reloadOnParamsChange ? [requestParamsJSON] : []),
        raiseErrors,
        setReload,
    ]);

    useEffect(() => {
        setLoaded(initiallyLoaded);
    }, [
        setLoaded,
        url,
        requestParamsJSON,
        initiallyLoaded,
    ]);

    let isLoading = loading;

    if (!remoteLoadingState && !loaded && !loading) {
        // Make sure that loading is not exported as false when the loading has not started yet
        isLoading = true;
    }

    return [
        dataState, isLoading, storeAction, error,
    ];
};

export default useData;
